import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, RefreshCw, Globe, Mail, Building2, User, Download, XCircle, ShieldAlert, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../components/ui/Toast';
import { DomainWhois, ApiResponse, WhoisExposureKeyword, ExposedDomain, ExposureStats, ReverseWhoisField, ReverseWhoisResponse } from '../types';
import { format, formatDistanceToNow } from 'date-fns';
import { LivenessBadge } from '../components/LivenessBadge';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';

interface BulkProgress {
  status: 'running' | 'completed' | 'cancelled';
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
  skipped: number;
  current_domain: string | null;
  errors: { domain_name: string; error: string }[];
  started_at: string;
  finished_at: string | null;
}

interface LookupStateStats {
  ok: number;
  error: number;
  skipped: number;
  never: number;
  pending: number;
}

export default function WhoisSearchPage() {
  const { fetchApi, isAdmin } = useApi();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DomainWhois[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [stats, setStats] = useState<{ total_records: number; with_email: number; last_fetched: string | null } | null>(null);
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  const [lookupStats, setLookupStats] = useState<LookupStateStats | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Reverse WHOIS state ---
  const [reverseField, setReverseField] = useState<ReverseWhoisField>('email');
  const [reverseQuery, setReverseQuery] = useState('');
  const [reverseData, setReverseData] = useState<ReverseWhoisResponse | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reverseActiveQuery, setReverseActiveQuery] = useState<{ field: ReverseWhoisField; query: string } | null>(null);

  const doReverseSearch = useCallback(async (field: ReverseWhoisField, q: string, page: number) => {
    const query = q.trim();
    if (!query) return;
    setReverseLoading(true);
    setReverseError(null);
    try {
      const res = await fetchApi<ApiResponse<ReverseWhoisResponse>>('/whois/reverse', {
        method: 'POST',
        body: JSON.stringify({ field, query, page }),
      });
      if (res.data) setReverseData(res.data);
      setReverseActiveQuery({ field, query });
    } catch (err) {
      setReverseData(null);
      setReverseError(err instanceof Error ? err.message : 'Reverse WHOIS search failed');
    } finally {
      setReverseLoading(false);
    }
  }, [fetchApi]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<{ total_records: number; with_email: number; last_fetched: string | null }>>('/whois/stats');
      if (res.data) setStats(res.data);
    } catch {
      // ignore
    }
    try {
      const res = await fetchApi<ApiResponse<LookupStateStats>>('/whois/lookup-stats');
      if (res.data) setLookupStats(res.data);
    } catch {
      // ignore
    }
  }, [fetchApi]);

  const pollProgress = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<BulkProgress | null>>('/whois/lookup-all/progress');
      if (res.data) {
        setBulkProgress(res.data);
        if (res.data.status !== 'running') {
          // Stop polling when done
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          // Refresh stats
          loadStats();
        }
      } else {
        setBulkProgress(null);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch {
      // ignore
    }
  }, [fetchApi, loadStats]);

  // On mount: load stats and check for in-progress bulk lookup
  useEffect(() => {
    loadStats();
    pollProgress();
  }, [loadStats, pollProgress]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startBulkLookup = async (mode: 'missing' | 'all' | 'exposed') => {
    setBulkStarting(true);
    try {
      const res = await fetchApi<ApiResponse<BulkProgress | { message: string; total: number }>>('/whois/lookup-all', {
        method: 'POST',
        body: JSON.stringify({ mode }),
      });

      if (res.data && 'status' in res.data) {
        setBulkProgress(res.data as BulkProgress);
        // Start polling
        pollRef.current = setInterval(pollProgress, 2000);
      } else if (res.data && 'message' in res.data) {
        toast.info((res.data as { message: string }).message);
      }
    } catch (error) {
      console.error('Failed to start bulk lookup:', error);
    } finally {
      setBulkStarting(false);
    }
  };

  const cancelBulkLookup = async () => {
    try {
      await fetchApi('/whois/lookup-all/cancel', { method: 'POST' });
    } catch {
      // ignore
    }
  };

  const doSearch = useCallback(async (searchQuery: string, searchPage: number) => {
    if (searchQuery.trim().length < 2) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetchApi<ApiResponse<DomainWhois[]>>(
        `/whois/search?q=${encodeURIComponent(searchQuery.trim())}&page=${searchPage}&limit=50`
      );
      setResults(res.data || []);
      setTotal(res.meta?.total || 0);
      setPage(searchPage);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(query, 1);
  };

  // --- Exposure Detection State ---
  const [exposureStats, setExposureStats] = useState<ExposureStats | null>(null);
  const [exposureExpanded, setExposureExpanded] = useState(false);
  const [keywords, setKeywords] = useState<WhoisExposureKeyword[]>([]);
  const [exposedDomains, setExposedDomains] = useState<ExposedDomain[]>([]);
  const [exposedTotal, setExposedTotal] = useState(0);
  const [exposedPage, setExposedPage] = useState(1);
  const [exposureLoading, setExposureLoading] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newCategory, setNewCategory] = useState<string>('');
  type ExposedSort = 'registrar' | 'domain' | 'liveness' | 'type';
  // Live sites first by default: content served under an exposed identity is the bigger risk.
  const [exposedSort, setExposedSort] = useState<ExposedSort>('liveness');
  const [exposedLive, setExposedLive] = useState(0);
  const [checkingIds, setCheckingIds] = useState<Set<string>>(new Set());

  const loadExposureStats = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<ExposureStats>>('/whois/exposure/stats');
      if (res.data) setExposureStats(res.data);
    } catch {
      // ignore
    }
  }, [fetchApi]);

  const loadKeywords = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<WhoisExposureKeyword[]>>('/whois/exposure/keywords');
      if (res.data) setKeywords(res.data);
    } catch {
      // ignore
    }
  }, [fetchApi]);

  const loadExposedDomains = useCallback(async (pg: number, sort: ExposedSort = exposedSort) => {
    setExposureLoading(true);
    try {
      const res = await fetchApi<ApiResponse<ExposedDomain[]>>(`/whois/exposure/domains?page=${pg}&limit=50&sort=${sort}`);
      setExposedDomains(res.data || []);
      setExposedTotal(res.meta?.total || 0);
      setExposedLive(res.meta?.live || 0);
      setExposedPage(pg);
    } catch {
      // ignore
    } finally {
      setExposureLoading(false);
    }
  }, [fetchApi, exposedSort]);

  const changeExposedSort = (sort: ExposedSort) => {
    setExposedSort(sort);
    loadExposedDomains(1, sort);
  };

  // Re-probe one domain's site now (admin), then refresh the current page.
  const checkLivenessNow = async (domainId: string) => {
    setCheckingIds((prev) => new Set(prev).add(domainId));
    try {
      await fetchApi(`/liveness/domain/${domainId}/check`, { method: 'POST' });
      await loadExposedDomains(exposedPage);
    } catch {
      // ignore — the row keeps its previous status
    } finally {
      setCheckingIds((prev) => { const next = new Set(prev); next.delete(domainId); return next; });
    }
  };

  useEffect(() => {
    loadExposureStats();
    loadKeywords();
  }, [loadExposureStats, loadKeywords]);

  useEffect(() => {
    if (exposureExpanded) {
      loadExposedDomains(1);
    }
  }, [exposureExpanded, loadExposedDomains]);

  // Refresh exposure data when a bulk rescan completes
  useEffect(() => {
    if (bulkProgress?.status === 'completed') {
      loadExposureStats();
      if (exposureExpanded) loadExposedDomains(exposedPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkProgress?.status]);

  const addKeyword = async () => {
    if (!newKeyword.trim()) return;
    try {
      const res = await fetchApi<ApiResponse<WhoisExposureKeyword>>('/whois/exposure/keywords', {
        method: 'POST',
        body: JSON.stringify({ keyword: newKeyword.trim(), category: newCategory || undefined }),
      });
      if (res.data) {
        setKeywords((prev) => [res.data!, ...prev]);
        setNewKeyword('');
        setNewCategory('');
        loadExposureStats();
        if (exposureExpanded) loadExposedDomains(1);
      }
    } catch (error: unknown) {
      toast.error('Failed to add keyword', error instanceof Error ? error.message : undefined);
    }
  };

  const removeKeyword = async (id: string) => {
    try {
      await fetchApi(`/whois/exposure/keywords/${id}`, { method: 'DELETE' });
      setKeywords((prev) => prev.filter((k) => k.id !== id));
      loadExposureStats();
      if (exposureExpanded) loadExposedDomains(1);
    } catch {
      // ignore
    }
  };

  const totalPages = Math.ceil(total / 50);
  const exposedTotalPages = Math.ceil(exposedTotal / 50);
  const isRunning = bulkProgress?.status === 'running';

  return (
    <div>
      <div className="mb-6">
        <PageHeader
          title="WHOIS Search"
          description="Search across WHOIS records for all domains in your inventory"
          actions={isAdmin && !isRunning && (
            <div className="flex gap-2">
              <button
                onClick={() => startBulkLookup('missing')}
                disabled={bulkStarting}
                className="btn btn-secondary text-sm"
                title="Fetch WHOIS for never-tried domains and retry transient errors (skipped domains are excluded)"
              >
                <Download className="h-4 w-4 mr-1.5" />
                {bulkStarting ? 'Starting...' : 'Fetch Pending'}
              </button>
              <button
                onClick={() => startBulkLookup('all')}
                disabled={bulkStarting}
                className="btn btn-primary text-sm"
                title="Refresh WHOIS for all domains"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                {bulkStarting ? 'Starting...' : 'Fetch All'}
              </button>
            </div>
          )}
        />
      </div>

      {/* Lookup-state breakdown */}
      {isAdmin && lookupStats && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-4">
          <span className="text-green-600 font-medium">{lookupStats.ok.toLocaleString()} looked up</span>
          {lookupStats.pending > 0 && (
            <span className="text-amber-600">{lookupStats.pending.toLocaleString()} pending</span>
          )}
          {lookupStats.error > 0 && (
            <span className="text-red-500">{lookupStats.error.toLocaleString()} errored (will retry)</span>
          )}
          {lookupStats.skipped > 0 && (
            <span
              className="text-slate-400"
              title="Domains the WHOIS provider can't audit (unsupported TLD, invalid/IDN name, or registry with no WHOIS). Excluded from retries."
            >
              {lookupStats.skipped.toLocaleString()} skipped
            </span>
          )}
        </div>
      )}

      {/* Bulk progress */}
      {bulkProgress && (bulkProgress.status === 'running' || bulkProgress.completed > 0) && (
        <BulkProgressCard progress={bulkProgress} onCancel={cancelBulkLookup} />
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="card px-4 py-3">
            <div className="text-xs text-slate-500 uppercase tracking-wider">WHOIS Records</div>
            <div className="text-2xl font-bold text-slate-900">{stats.total_records}</div>
          </div>
          <div className="card px-4 py-3">
            <div className="text-xs text-slate-500 uppercase tracking-wider">With Email</div>
            <div className="text-2xl font-bold text-slate-900">{stats.with_email}</div>
          </div>
          <div className="card px-4 py-3">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Last Fetched</div>
            <div className="text-sm font-medium text-slate-900 mt-1">
              {stats.last_fetched
                ? format(new Date(stats.last_fetched), 'MMM d, yyyy h:mm a')
                : 'Never'}
            </div>
          </div>
          <div
            className="card px-4 py-3 cursor-pointer hover:ring-2 hover:ring-red-200 transition-all"
            onClick={() => setExposureExpanded(!exposureExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="text-xs text-red-600 uppercase tracking-wider font-medium flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" />
                Exposed Domains
              </div>
              {exposureExpanded ? (
                <ChevronDown className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-400" />
              )}
            </div>
            <div className="text-2xl font-bold text-red-600">
              {exposureStats?.total_exposed ?? 0}
            </div>
            <div className="text-xs text-slate-400">
              {exposureStats?.total_keywords ?? 0} keyword{(exposureStats?.total_keywords ?? 0) !== 1 ? 's' : ''} configured
            </div>
          </div>
        </div>
      )}

      {/* Exposure Detection Section */}
      {exposureExpanded && (
        <div className="card p-4 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <ShieldAlert className="h-5 w-5 text-red-500" />
            WHOIS Exposure Detection
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Define keywords (your name, email, company) to find domains where your personal information is exposed in WHOIS records.
          </p>

          {/* Keyword Management */}
          {isAdmin && (
            <div className="mb-4">
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add keyword (e.g., your name, email)..."
                  className="input flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                />
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="input w-40"
                >
                  <option value="">Category...</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="organization">Organization</option>
                  <option value="phone">Phone</option>
                  <option value="other">Other</option>
                </select>
                <button
                  onClick={addKeyword}
                  disabled={!newKeyword.trim()}
                  className="btn btn-primary text-sm"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </button>
              </div>
            </div>
          )}

          {/* Keywords list */}
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {keywords.map((kw) => (
                <span
                  key={kw.id}
                  className="inline-flex items-center gap-1.5 text-sm bg-red-50 text-red-700 px-2.5 py-1 rounded-md border border-red-200"
                >
                  {kw.keyword}
                  {kw.category && (
                    <span className="text-xs text-red-400">({kw.category})</span>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => removeKeyword(kw.id)}
                      className="text-red-400 hover:text-red-600 ml-0.5"
                      title="Remove keyword"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {keywords.length === 0 && (
            <EmptyState compact icon={ShieldAlert} title="No keywords configured yet" description="Add keywords above to detect WHOIS exposure." />
          )}

          {/* Exposed domains table */}
          {keywords.length > 0 && (
            <>
              {exposureLoading ? (
                <table className="w-full text-sm"><tbody><SkeletonRows rows={5} cols={6} /></tbody></table>
              ) : exposedDomains.length === 0 ? (
                <div className="text-sm text-green-600 text-center py-4 bg-green-50 rounded-md">
                  No exposed domains found. All WHOIS records are protected from your keywords.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-slate-500">
                      {exposedTotal} exposed domain{exposedTotal !== 1 ? 's' : ''} found
                      {exposedLive > 0 && (
                        <span className="ml-2 text-red-600 font-medium">· {exposedLive} live</span>
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => startBulkLookup('exposed')}
                        disabled={bulkStarting || isRunning}
                        className="btn btn-secondary text-sm"
                        title="Re-fetch WHOIS for all exposed domains"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${isRunning ? 'animate-spin' : ''}`} />
                        {isRunning ? 'Rescanning…' : bulkStarting ? 'Starting…' : 'Rescan All Exposed'}
                      </button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <button
                              onClick={() => changeExposedSort('domain')}
                              className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 ${exposedSort === 'domain' ? 'text-slate-900' : ''}`}
                            >
                              Domain
                              {exposedSort === 'domain' && <ChevronDown className="h-3 w-3" />}
                            </button>
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <button
                              onClick={() => changeExposedSort('liveness')}
                              className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 ${exposedSort === 'liveness' ? 'text-slate-900' : ''}`}
                              title="Sort live sites first"
                            >
                              Site
                              {exposedSort === 'liveness' && <ChevronDown className="h-3 w-3" />}
                            </button>
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <button
                              onClick={() => changeExposedSort('type')}
                              className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 ${exposedSort === 'type' ? 'text-slate-900' : ''}`}
                            >
                              Type
                              {exposedSort === 'type' && <ChevronDown className="h-3 w-3" />}
                            </button>
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Matched Keyword</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Field</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <button
                              onClick={() => changeExposedSort('registrar')}
                              className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 ${exposedSort === 'registrar' ? 'text-slate-900' : ''}`}
                            >
                              Registrar
                              {exposedSort === 'registrar' && <ChevronDown className="h-3 w-3" />}
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {exposedDomains.map((ed, i) => (
                          <tr key={`${ed.domain_id}-${ed.matched_keyword}-${ed.matched_field}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3">
                              <Link
                                to={`/domains/${ed.domain_id}`}
                                className="text-brand-600 hover:text-brand-800 font-medium"
                              >
                                {ed.domain_name}
                              </Link>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <span
                                className="inline-flex items-center gap-1.5"
                                title={[
                                  ed.liveness_final_url && `Final URL: ${ed.liveness_final_url}`,
                                  ed.liveness_checked_at
                                    ? `Checked ${formatDistanceToNow(new Date(ed.liveness_checked_at), { addSuffix: true })}`
                                    : 'Never checked',
                                ].filter(Boolean).join('\n')}
                              >
                                <LivenessBadge status={ed.liveness_status} />
                                {isAdmin && (
                                  <button
                                    onClick={() => checkLivenessNow(ed.domain_id)}
                                    disabled={checkingIds.has(ed.domain_id)}
                                    className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                                    title="Check site now"
                                  >
                                    <RefreshCw className={`h-3.5 w-3.5 ${checkingIds.has(ed.domain_id) ? 'animate-spin' : ''}`} />
                                  </button>
                                )}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                              {ed.domain_type || <span className="text-slate-300">-</span>}
                            </td>
                            <td className="py-2 px-3">
                              <span className="inline-flex items-center text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded">
                                {ed.matched_keyword}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-slate-600">
                              {formatFieldName(ed.matched_field)}
                            </td>
                            <td className="py-2 px-3 text-slate-500">
                              {ed.registrar_name || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {exposedTotalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <button
                        onClick={() => loadExposedDomains(exposedPage - 1)}
                        disabled={exposedPage <= 1}
                        className="btn btn-secondary text-sm"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-slate-500">
                        Page {exposedPage} of {exposedTotalPages}
                      </span>
                      <button
                        onClick={() => loadExposedDomains(exposedPage + 1)}
                        disabled={exposedPage >= exposedTotalPages}
                        className="btn btn-secondary text-sm"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Reverse WHOIS — admin only (credit-spending Whoxy reverse lookups) */}
      {isAdmin && (
      <div className="card mb-6">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="h-5 w-5 text-slate-400" />
            Reverse WHOIS
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Find domains registered under a name, email, company, or keyword (via Whoxy), and see which you don't own.
          </p>
        </div>
        <div className="p-4">
          <form
            onSubmit={(e) => { e.preventDefault(); doReverseSearch(reverseField, reverseQuery, 1); }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <select
              value={reverseField}
              onChange={(e) => setReverseField(e.target.value as ReverseWhoisField)}
              className="input sm:w-40"
            >
              <option value="email">Email</option>
              <option value="name">Name</option>
              <option value="company">Company</option>
              <option value="keyword">Keyword</option>
            </select>
            <input
              type="text"
              value={reverseQuery}
              onChange={(e) => setReverseQuery(e.target.value)}
              placeholder={`Search by ${reverseField}...`}
              className="input flex-1"
            />
            <button type="submit" disabled={reverseLoading || !reverseQuery.trim()} className="btn btn-primary">
              {reverseLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> : <Search className="h-4 w-4 mr-1.5" />}
              Search
            </button>
          </form>

          {reverseError && (
            <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{reverseError}</span>
            </div>
          )}

          {reverseData && !reverseError && (
            <div className="mt-4">
              <p className="text-sm text-slate-500 mb-2">
                <span className="font-medium text-slate-700">{reverseData.total_results.toLocaleString()}</span> total matches
                {' · '}
                <span className="font-medium text-slate-700">{reverseData.owned_on_page}</span> of {reverseData.results.length} on this page in your inventory
              </p>
              {reverseData.results.length === 0 ? (
                <EmptyState compact icon={Search} title="No domains found for this query" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200">
                        <th className="px-3 py-2">Domain</th>
                        <th className="px-3 py-2">Inventory</th>
                        <th className="px-3 py-2">Created</th>
                        <th className="px-3 py-2">Expires</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {reverseData.results.map((r) => (
                        <tr key={r.domain_name} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium">
                            {r.owned && r.domain_id ? (
                              <Link to={`/domains/${r.domain_id}`} className="text-brand-600 hover:text-brand-800">{r.domain_name}</Link>
                            ) : (
                              <span className="text-slate-700">{r.domain_name}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {r.owned ? (
                              <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-md border text-green-700 bg-green-50 border-green-200">Owned</span>
                            ) : (
                              <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-md border text-slate-500 bg-slate-50 border-slate-200">Not owned</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{r.create_date || '-'}</td>
                          <td className="px-3 py-2 text-slate-500">{r.expiry_date || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {reverseData.total_pages > 1 && reverseActiveQuery && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    onClick={() => doReverseSearch(reverseActiveQuery.field, reverseActiveQuery.query, reverseData.current_page - 1)}
                    disabled={reverseData.current_page <= 1 || reverseLoading}
                    className="btn btn-secondary text-sm"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-500">Page {reverseData.current_page} of {reverseData.total_pages}</span>
                  <button
                    onClick={() => doReverseSearch(reverseActiveQuery.field, reverseActiveQuery.query, reverseData.current_page + 1)}
                    disabled={reverseData.current_page >= reverseData.total_pages || reverseLoading}
                    className="btn btn-secondary text-sm"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Search form */}
      <form onSubmit={handleSubmit} className="card p-4 mb-6">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email, name, organization, phone, registrar..."
              className="input pl-10 w-full"
            />
          </div>
          <button
            type="submit"
            disabled={loading || query.trim().length < 2}
            className="btn btn-primary"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Search'}
          </button>
        </div>
      </form>

      {/* Results */}
      {searched && !loading && results.length === 0 && (
        <div className="card">
          <EmptyState
            icon={Search}
            title={`No WHOIS records found matching "${query}"`}
            description="Make sure you've fetched WHOIS data for your domains first."
          />
        </div>
      )}

      {results.length > 0 && (
        <>
          <div className="text-sm text-slate-500 mb-3">
            {total} result{total !== 1 ? 's' : ''} found
          </div>
          <div className="space-y-3">
            {results.map((record) => (
              <WhoisResultCard key={record.id} record={record} searchQuery={query} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => doSearch(query, page - 1)}
                disabled={page <= 1}
                className="btn btn-secondary text-sm"
              >
                Previous
              </button>
              <span className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => doSearch(query, page + 1)}
                disabled={page >= totalPages}
                className="btn btn-secondary text-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BulkProgressCard({ progress, onCancel }: { progress: BulkProgress; onCancel: () => void }) {
  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  const isRunning = progress.status === 'running';

  return (
    <div className="card p-4 mb-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isRunning && <RefreshCw className="h-4 w-4 text-brand-600 animate-spin" />}
          <span className="text-sm font-medium text-slate-700">
            {isRunning ? 'Bulk WHOIS Lookup in Progress' :
             progress.status === 'completed' ? 'Bulk Lookup Complete' :
             'Bulk Lookup Cancelled'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            {progress.completed} / {progress.total}
          </span>
          {isRunning && (
            <button onClick={onCancel} className="text-slate-400 hover:text-red-500 transition-colors" title="Cancel">
              <XCircle className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${
            progress.status === 'completed' ? 'bg-green-500' :
            progress.status === 'cancelled' ? 'bg-yellow-500' :
            'bg-brand-600'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex gap-3">
          <span className="text-green-600">{progress.succeeded} succeeded</span>
          {progress.failed > 0 && (
            <span className="text-red-500">{progress.failed} failed</span>
          )}
          {progress.skipped > 0 && (
            <span className="text-slate-400">{progress.skipped} skipped</span>
          )}
        </div>
        {isRunning && progress.current_domain && (
          <span className="text-slate-400 truncate ml-4">
            Looking up {progress.current_domain}...
          </span>
        )}
      </div>

      {/* Show errors if any */}
      {progress.errors.length > 0 && progress.status !== 'running' && (
        <details className="mt-3">
          <summary className="text-xs text-red-500 cursor-pointer hover:text-red-600">
            {progress.errors.length} error{progress.errors.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-1 max-h-32 overflow-y-auto">
            {progress.errors.map((err, i) => (
              <div key={i} className="text-xs text-slate-500 py-0.5">
                <span className="font-mono">{err.domain_name}</span>: {err.error}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function formatFieldName(field: string): string {
  const map: Record<string, string> = {
    registrant_name: 'Registrant Name',
    registrant_organization: 'Registrant Org',
    registrant_email: 'Registrant Email',
    registrant_phone: 'Registrant Phone',
    admin_name: 'Admin Name',
    admin_email: 'Admin Email',
    admin_phone: 'Admin Phone',
    tech_name: 'Tech Name',
    tech_email: 'Tech Email',
    tech_phone: 'Tech Phone',
  };
  return map[field] || field;
}

function WhoisResultCard({ record, searchQuery }: { record: DomainWhois; searchQuery: string }) {
  const highlight = (text: string | null) => {
    if (!text || !searchQuery) return text;
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to={`/domains/${record.domain_id}`}
            className="text-lg font-semibold text-brand-600 hover:text-brand-800"
          >
            {record.domain_name}
          </Link>
          {record.registrar_name && (
            <span className="text-sm text-slate-400 ml-2">{record.registrar_name}</span>
          )}
        </div>
        <div className="text-xs text-slate-400 whitespace-nowrap">
          Fetched {record.last_fetched ? format(new Date(record.last_fetched), 'MMM d, yyyy') : 'N/A'}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 [&>*]:min-w-0 [&>*]:[overflow-wrap:anywhere]">
        {/* Registrant */}
        {(record.registrant_name || record.registrant_email || record.registrant_organization) && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              <User className="h-3 w-3" /> Registrant
            </div>
            {record.registrant_name && (
              <div className="text-sm text-slate-700">{highlight(record.registrant_name)}</div>
            )}
            {record.registrant_organization && (
              <div className="text-sm text-slate-600 flex items-center gap-1">
                <Building2 className="h-3 w-3 text-slate-400" />
                {highlight(record.registrant_organization)}
              </div>
            )}
            {record.registrant_email && (
              <div className="text-sm text-slate-600 flex items-center gap-1">
                <Mail className="h-3 w-3 text-slate-400" />
                {highlight(record.registrant_email)}
              </div>
            )}
            {record.registrant_country && (
              <div className="text-xs text-slate-400 mt-0.5">
                {[record.registrant_city, record.registrant_state, record.registrant_country].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Admin */}
        {(record.admin_name || record.admin_email) && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              <User className="h-3 w-3" /> Admin
            </div>
            {record.admin_name && (
              <div className="text-sm text-slate-700">{highlight(record.admin_name)}</div>
            )}
            {record.admin_email && (
              <div className="text-sm text-slate-600 flex items-center gap-1">
                <Mail className="h-3 w-3 text-slate-400" />
                {highlight(record.admin_email)}
              </div>
            )}
          </div>
        )}

        {/* Tech */}
        {(record.tech_name || record.tech_email) && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              <User className="h-3 w-3" /> Tech
            </div>
            {record.tech_name && (
              <div className="text-sm text-slate-700">{highlight(record.tech_name)}</div>
            )}
            {record.tech_email && (
              <div className="text-sm text-slate-600 flex items-center gap-1">
                <Mail className="h-3 w-3 text-slate-400" />
                {highlight(record.tech_email)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* WHOIS Registrar & Status */}
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
        {record.whois_registrar && (
          <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
            <Globe className="h-3 w-3" />
            {highlight(record.whois_registrar)}
          </span>
        )}
        {record.status_codes && (() => {
          try {
            const codes: string[] = JSON.parse(record.status_codes);
            return codes.slice(0, 3).map((code, i) => (
              <span key={i} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                {code.split(' ')[0]}
              </span>
            ));
          } catch { return null; }
        })()}
      </div>
    </div>
  );
}
